import { Toast } from '../../../../components/Common/Toast';
import { callAPI } from '../../../../services/api.service';
import {
  FulfillmentActionNeeded,
  OrderFulfillmentStatusEnum,
} from '../../../../interfaces/order.interface';

export type OrdersChip = 'needs_attention' | 'waiting' | 'in_progress' | 'ready_to_dispatch' | 'stuck' | 'shipped' | 'canceled';

export interface GlobalOrdersParams {
  status?: string[];
  orderType?: string;
  country?: string;
  dateFrom?: string;
  dateTo?: string;
  chip?: OrdersChip | '';
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  includeLegacy?: boolean;
}

export interface GlobalOrderRow {
  id: string;
  ownerEmail: string | null;
  orderNumber: string;
  orderType: string;
  bookId: string | null;
  bookTitle: string | null;
  quantity: number | null;
  shippingCountry: string | null;
  fulfillmentStatus: OrderFulfillmentStatusEnum;
  actionNeeded: FulfillmentActionNeeded;
  trackingNumber: string | null;
  carrier: string | null;
  printBatchId: string | null;
  dispatchHold: boolean;
  waitToShip: boolean;
  /** Print-readiness of the ordered book (ship-guard rule). null = no book / not found. */
  bookPrintReady?: boolean | null;
  bookPrintReadyReasons?: string[];
  timestamp: string | null;
  order: Record<string, any>;
}

export interface GlobalOrdersResult {
  rows: GlobalOrderRow[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  truncated: boolean;
}

export interface OrderCounts {
  byType: Record<string, number>;
  byGroup: Record<string, number>;
  /** The orders-track-from-date cutoff (YYYY-MM-DD) — null when unset or when legacy is included. */
  trackFrom: string | null;
}

// Badge counts for the type tabs (byType, global) and fulfillment chips (byGroup, scoped to the
// given order type). Silent on failure — the tabs/chips still work without their counts.
export const fetchGlobalOrderCounts = async (orderType?: string, includeLegacy?: boolean): Promise<OrderCounts | null> => {
  try {
    const qs = new URLSearchParams();
    if (orderType) qs.set('orderType', orderType);
    if (includeLegacy) qs.set('includeLegacy', 'true');
    const query = qs.toString();
    const response = await callAPI(`admin/orders/counts${query ? `?${query}` : ''}`);
    if (response?.byType) {
      return { byType: response.byType, byGroup: response.byGroup || {}, trackFrom: response.trackFrom ?? null };
    }
    return null;
  } catch {
    return null;
  }
};

export const fetchGlobalOrders = async (params: GlobalOrdersParams): Promise<GlobalOrdersResult | null> => {
  try {
    const qs = new URLSearchParams();
    if (params.status?.length) qs.set('status', params.status.join(','));
    if (params.orderType) qs.set('orderType', params.orderType);
    if (params.country) qs.set('country', params.country);
    if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params.dateTo) qs.set('dateTo', params.dateTo);
    if (params.chip) qs.set('chip', params.chip);
    if (params.search) qs.set('search', params.search);
    if (params.includeLegacy) qs.set('includeLegacy', 'true');
    qs.set('pageNumber', String(params.pageNumber || 1));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));

    const response = await callAPI(`admin/orders/list?${qs.toString()}`);
    if (response?.rows) {
      return response as GlobalOrdersResult;
    }
    Toast(response?.error || 'Failed to load orders', true);
    return null;
  } catch (error) {
    Toast('Failed to load orders', true);
    return null;
  }
};
